import { ref, onMounted, onUnmounted } from 'vue'

const ONE_MINUTE_MS = 60 * 1000
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

/**
 * Provides a reactive `now` ref that ticks every minute (client-only).
 * Timer is started in onMounted to avoid leaking intervals during SSR.
 */
export function useTickingNow() {
  const now = ref(Date.now())
  let timer: ReturnType<typeof setInterval> | null = null

  onMounted(() => {
    now.value = Date.now()
    timer = setInterval(() => {
      now.value = Date.now()
    }, ONE_MINUTE_MS)
  })

  onUnmounted(() => {
    if (timer) clearInterval(timer)
  })

  return now
}

/** Earliest ISO unlock time among categories still locked by drip (`unlocked === false`). */
export function getEarliestLockedDripAvailableAt(
  dripCategoryUnlockedMap: Record<string, boolean>,
  dripCategoryAvailableAtMap: Record<string, string> | undefined
): string | null {
  if (!dripCategoryAvailableAtMap) return null

  let earliest: string | null = null
  let earliestMs = Infinity

  for (const [categoryId, availableAt] of Object.entries(dripCategoryAvailableAtMap)) {
    if (dripCategoryUnlockedMap[categoryId] !== false || !availableAt) continue
    const ms = new Date(availableAt).getTime()
    if (Number.isNaN(ms) || ms >= earliestMs) continue
    earliestMs = ms
    earliest = availableAt
  }

  return earliest
}

/**
 * Formats a drip countdown label when the unlock is within 24 hours.
 * Returns null if unavailable, already unlocked, or more than 24hrs away.
 */
export function getDripCountdownLabel(
  availableAt: string | null | undefined,
  nowMs: number,
  t: (key: string, params?: Record<string, any>) => string,
  countdownKey: string
): string | null {
  if (!availableAt) return null

  const diff = new Date(availableAt).getTime() - nowMs
  if (diff <= 0) return null
  if (diff > TWENTY_FOUR_HOURS_MS) return null

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  return t(countdownKey, { hours, minutes })
}
